import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const MemoryForm = (props) => {
  const { memoryTypes, initialMemoryType, initialMemoryData, formAction, formMethod, csrfToken, cancelUrl, childName, isEditing } = props;
  const [memoryType, setMemoryType] = useState(initialMemoryType || memoryTypes[0][1]);
  const [memoryData, setMemoryData] = useState(initialMemoryData || '');
  
  const handleMemoryDataChange = (e) => {
    setMemoryData(e.target.value);
  };
  
  const hints = {
    math: "Describe your child's relationship with math. Are they struggling with specific concepts like fractions or multiplication? Do they excel at certain areas? What approaches help them understand math better?",
    science: "Share your child's interests in science. Do they love space, animals, or experiments? Are there specific topics they're curious about or find challenging?",
    history: "Note your child's engagement with history. Are they fascinated by certain time periods or historical figures? Do they connect better with history through stories or visual timelines?",
    language_arts: "Describe your child's reading and writing abilities. What types of books do they enjoy? Are there specific grammar or vocabulary areas they find difficult?",
    learning_style: "How does your child learn best? Do they prefer visual demonstrations, hands-on activities, or verbal explanations? Do they need frequent breaks or prefer to focus for longer periods?",
    interests: "What are your child's hobbies, favorite characters, or topics they get excited about? This helps our AI use relevant examples that will engage them.",
    strengths: "What subjects or skills does your child excel at? What makes them proud of themselves?",
    challenges: "What areas does your child find difficult? Are there specific learning obstacles we should be aware of?",
    anything: "Share anything that would help our AI better understand how your child learns and what engages them. This could include interests, learning preferences, or specific needs."
  };
  
  const placeholders = {
    math: `For example: ${childName} struggles with fractions but enjoys geometry. They learn math concepts better when using visual aids and real-world examples.`,
    science: `For example: ${childName} is fascinated by dinosaurs and space exploration. They enjoy hands-on experiments but need help understanding scientific terminology.`,
    history: `For example: ${childName} loves learning about ancient Egypt and the Roman Empire. They remember historical events better when presented as stories about people.`,
    language_arts: `For example: ${childName} enjoys reading adventure books but struggles with writing longer essays. They have a strong vocabulary but need help with grammar.`,
    learning_style: `For example: ${childName} is a visual learner who benefits from diagrams and videos. They need frequent breaks during study sessions and prefer learning in a quiet environment.`,
    interests: `For example: ${childName} loves everything about dinosaurs, especially T-Rex and Triceratops. They also enjoy building with Legos and playing Minecraft.`,
    strengths: `For example: ${childName} has a great memory for facts and details. They're particularly good at mental math and creative storytelling.`,
    challenges: `For example: ${childName} finds it difficult to focus for long periods. They struggle with organizing their thoughts when writing and need extra help with spelling.`,
    anything: `For example: ${childName} loves dinosaurs.`
  };
  
  const questions = {
    math: `How does ${childName} feel about math? What concepts do they enjoy or struggle with?`,
    science: `What scientific topics interest ${childName}? How do they prefer to learn about science?`,
    history: `What historical periods or figures fascinate ${childName}? How do they best engage with history?`,
    language_arts: `What are ${childName}'s reading and writing habits? What types of books or writing activities do they enjoy?`,
    learning_style: `How does ${childName} learn most effectively? What environment or teaching approaches work best?`,
    interests: `What topics, activities, or characters does ${childName} get excited about?`,
    strengths: `What subjects or skills does ${childName} excel at? What makes them feel confident?`,
    challenges: `What areas does ${childName} find challenging? How can we better support their learning?`,
    anything: `What would help our AI better understand ${childName}'s unique learning style and interests?`
  };
  
  const handleMemoryTypeChange = (e) => {
    setMemoryType(e.target.value);
  };
  
  return (
    <div>
      <div className="bg-blue-50 rounded-xl p-5 mb-6 border border-blue-100">
        <h3 className="text-lg font-medium text-blue-800 mb-2">What would you like to tell us about {childName}?</h3>
        <p className="text-blue-700">{questions[memoryType] || questions.anything}</p>
      </div>
      
      <form action={formAction} method="post" className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <input type="hidden" name="authenticity_token" value={csrfToken} />
        {isEditing && <input type="hidden" name="_method" value="patch" />}
        
        <div className="space-y-6">
          <div className="flex flex-col">
            <label htmlFor="user_ai_memory_memory_type" className="text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select 
              id="user_ai_memory_memory_type" 
              name="user_ai_memory[memory_type]" 
              className={`rounded-lg border-gray-300 focus:ring-yellow-500 focus:border-yellow-500 ${isEditing ? 'bg-gray-100' : ''}`}
              value={memoryType}
              onChange={handleMemoryTypeChange}
              disabled={isEditing}
            >
              {memoryTypes.map(([label, value]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {isEditing ? 
                "Memory category cannot be changed. Delete this memory and create a new one to use a different category." : 
                "Select the category that best fits this memory"}
            </p>
          </div>
          
          <div className="flex flex-col">
            <label htmlFor="user_ai_memory_memory_data" className="text-sm font-medium text-gray-700 mb-1">
              Content
            </label>
            <textarea 
              id="user_ai_memory_memory_data" 
              name="user_ai_memory[memory_data]" 
              className="rounded-lg border-gray-300 focus:ring-yellow-500 focus:border-yellow-500"
              rows="4"
              placeholder={placeholders[memoryType] || placeholders.anything}
              value={memoryData}
              onChange={handleMemoryDataChange}
            ></textarea>
          </div>
          
          <div className="mt-2 text-sm text-blue-600 italic bg-blue-50 p-3 rounded-lg">
            <span className="font-medium">Suggestion: </span>
            <span>{hints[memoryType] || hints.anything}</span>
          </div>
        </div>
        
        <div className="flex justify-between mt-8">
          <a href={cancelUrl} className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg">
            Cancel
          </a>
          <button type="submit" className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg shadow-md">
            {isEditing ? "Update Memory" : "Save Memory"}
          </button>
        </div>
      </form>
    </div>
  );
};

MemoryForm.propTypes = {
  memoryTypes: PropTypes.array.isRequired,
  initialMemoryType: PropTypes.string,
  initialMemoryData: PropTypes.string,
  formAction: PropTypes.string.isRequired,
  formMethod: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  cancelUrl: PropTypes.string.isRequired,
  childName: PropTypes.string.isRequired,
  isEditing: PropTypes.bool
};

MemoryForm.defaultProps = {
  isEditing: false,
  initialMemoryData: ""
};

export default MemoryForm; 